<p-drawer
  [visible]="visible()"
  (visibleChange)="visibleChange.emit($event)"
  position="right"
  [style]="{ width: '1000px' }"
  [modal]="true"
  (onHide)="close()">

  <ng-template pTemplate="header">
    <div class="drawer-header">
      <i class="pi pi-list"></i>
      <h2 i18n="@@benchmarks.runDetails.title">Прогон бенчмарка</h2>
    </div>
  </ng-template>

  <div class="drawer-content">
    @if (isLoading()) {
      <div class="loading-container">
        <p-progressSpinner styleClass="loading-spinner"></p-progressSpinner>
      </div>
    } @else if (run(); as r) {
      <!-- Summary: parent run -->
      <div class="summary-card">
        <div class="summary-line">
          <p-tag [value]="r.status" [severity]="statusSeverity(r.status)" [rounded]="true"></p-tag>
          <p-tag [value]="triggerLabel(r.trigger)" severity="secondary" [rounded]="true"></p-tag>
          <p-tag [value]="(r.model_type_code || '').toUpperCase()" severity="info" [rounded]="true"></p-tag>
        </div>
        <div class="summary-line">
          <strong>{{ r.benchmark_title }}</strong>
        </div>
        <div class="summary-line dim">
          <span i18n="@@benchmarks.runDetails.started">Started</span>
          <span>{{ formatDate(r.created_at) }}</span>
          @if (r.recorded_at) {
            <span>·</span>
            <span i18n="@@benchmarks.runDetails.finished">Finished</span>
            <span>{{ formatDate(r.recorded_at) }}</span>
          }
        </div>
        @if (r.error_message) {
          <div class="summary-error">
            <i class="pi pi-exclamation-triangle"></i>
            <span>{{ r.error_message }}</span>
          </div>
        }
        <!-- Item counters — сколько версий в каких статусах. Раньше эти
             метрики были на run'e; теперь run — это агрегатор нескольких
             per-версионных items. -->
        <div class="counters-row">
          <div class="counter" pTooltip="Всего версий в прогоне"
               i18n-pTooltip="@@benchmarks.runDetails.counter.items" tooltipPosition="top">
            <i class="pi pi-server"></i>
            <span class="dim" i18n="@@benchmarks.runDetails.items">Items</span>
            <strong>{{ r.item_count ?? 0 }}</strong>
          </div>
          @if ((r.completed_count ?? 0) > 0) {
            <div class="counter counter-success" pTooltip="Завершены"
                 i18n-pTooltip="@@benchmarks.runDetails.counter.completed" tooltipPosition="top">
              <i class="pi pi-check-circle"></i>{{ r.completed_count }}
            </div>
          }
          @if ((r.running_count ?? 0) > 0) {
            <div class="counter counter-warn" pTooltip="В процессе"
                 i18n-pTooltip="@@benchmarks.runDetails.counter.running" tooltipPosition="top">
              <i class="pi pi-spin pi-spinner"></i>{{ r.running_count }}
            </div>
          }
          @if ((r.failed_count ?? 0) > 0) {
            <div class="counter counter-danger" pTooltip="С ошибкой"
                 i18n-pTooltip="@@benchmarks.runDetails.counter.failed" tooltipPosition="top">
              <i class="pi pi-times-circle"></i>{{ r.failed_count }}
            </div>
          }
          @if ((r.stopped_count ?? 0) > 0) {
            <div class="counter counter-secondary" pTooltip="Остановлены"
                 i18n-pTooltip="@@benchmarks.runDetails.counter.stopped" tooltipPosition="top">
              <i class="pi pi-stop-circle"></i>{{ r.stopped_count }}
            </div>
          }
          @if ((r.initial_count ?? 0) > 0) {
            <div class="counter" pTooltip="В очереди"
                 i18n-pTooltip="@@benchmarks.runDetails.counter.initial" tooltipPosition="top">
              <i class="pi pi-hourglass"></i>{{ r.initial_count }}
            </div>
          }
        </div>
      </div>

      <!-- Per-item breakdown — версии внутри run'а. Каждая раскрывается
           в per-iteration results через chevron справа. -->
      <div class="items-section">
        <h3 i18n="@@benchmarks.runDetails.byVersion">По версиям</h3>

        @for (row of items(); track row.item?.id) {
          @if (row.item; as it) {
            <div class="item-card">
              <div class="item-header" (click)="toggleItem(it.id)">
                <p-tag [value]="it.status" [severity]="statusSeverity(it.status)" [rounded]="true"></p-tag>
                <div class="item-model-line">
                  <strong>{{ it.model_name }}</strong>
                  <span class="dim">·</span>
                  <span>{{ it.model_version }}</span>
                  <span class="dim">·</span>
                  <span class="dim">{{ it.gateway_code }}</span>
                </div>

                <div class="item-metrics">
                  <span class="metric-inline" pTooltip="Успешно / всего сэмплов"
                        i18n-pTooltip="@@benchmarks.runDetails.metric.samples"
                        tooltipPosition="top">
                    <i class="pi pi-check-square"></i>
                    <span class="dim">{{ it.success_count ?? 0 }}</span>
                    <span class="dim">/</span>
                    <span class="dim">{{ it.sample_count ?? 0 }}</span>
                    @if ((it.failed_samples ?? 0) > 0) {
                      <span class="fail-count">✗{{ it.failed_samples }}</span>
                    }
                  </span>
                  @if (isLlm()) {
                    <span class="metric-inline" pTooltip="Time to first token (avg)"
                          i18n-pTooltip="@@benchmarks.runDetails.metric.ttft"
                          tooltipPosition="top">
                      <i class="pi pi-bolt"></i>{{ formatMs(it.avg_ttft_ms) }}
                    </span>
                    <span class="metric-inline" pTooltip="Tokens per second (avg)"
                          i18n-pTooltip="@@benchmarks.runDetails.metric.tps"
                          tooltipPosition="top">
                      <i class="pi pi-gauge"></i>{{ formatTps(it.avg_tokens_per_sec) }}
                    </span>
                  }
                  @if (isStt()) {
                    <span class="metric-inline" pTooltip="Word Error Rate"
                          i18n-pTooltip="@@benchmarks.runDetails.metric.wer"
                          tooltipPosition="top">
                      <i class="pi pi-percentage"></i>WER {{ formatErrorRate(it.wer) }}
                    </span>
                    <span class="metric-inline" pTooltip="Character Error Rate"
                          i18n-pTooltip="@@benchmarks.runDetails.metric.cer"
                          tooltipPosition="top">
                      <i class="pi pi-percentage"></i>CER {{ formatErrorRate(it.cer) }}
                    </span>
                    <span class="metric-inline" pTooltip="RTFx = длительность аудио / время обработки. 5× — распознали в 5 раз быстрее, чем длится файл. 1× — реалтайм. <1× — модель отстаёт."
                          i18n-pTooltip="@@benchmarks.runDetails.metric.rtfx"
                          tooltipPosition="top">
                      <i class="pi pi-forward"></i>{{ formatRtfx(it.avg_rtfx) }}
                    </span>
                  }
                </div>

                <i class="pi"
                   [class.pi-chevron-right]="!isItemExpanded(it.id)"
                   [class.pi-chevron-down]="isItemExpanded(it.id)"></i>
              </div>

              @if (it.error_message) {
                <div class="item-error">
                  <i class="pi pi-exclamation-triangle"></i>
                  <span>{{ it.error_message }}</span>
                </div>
              }

              @if (isItemExpanded(it.id)) {
                <div class="item-body">
                  @if (isLlm() && it.avg_ttft_ms != null) {
                    <div class="item-percentiles">
                      <div><span class="dim">TTFT avg/p50/p95</span>
                        {{ formatMs(it.avg_ttft_ms) }} <span class="dim">/</span>
                        {{ formatMs(it.p50_ttft_ms) }} <span class="dim">/</span>
                        {{ formatMs(it.p95_ttft_ms) }}
                      </div>
                      <div><span class="dim">Tokens/s avg/p50/p95</span>
                        {{ formatTps(it.avg_tokens_per_sec) }} <span class="dim">/</span>
                        {{ formatTps(it.p50_tokens_per_sec) }} <span class="dim">/</span>
                        {{ formatTps(it.p95_tokens_per_sec) }}
                      </div>
                    </div>
                  }

                  <p-table [value]="row.results ?? []" styleClass="results-table" [scrollable]="true" scrollHeight="360px">
                    <ng-template pTemplate="header">
                      <tr>
                        <th style="width: 3rem;">#</th>
                        <th style="width: 6rem;" i18n="@@benchmarks.runDetails.col.status">Status</th>
                        @if (isStt()) {
                          <th>
                            <i class="pi pi-microphone col-icon"></i>
                            <span i18n="@@benchmarks.runDetails.col.sample">Sample</span>
                          </th>
                          <th style="width: 6rem; text-align: right;" pTooltip="Word Error Rate"
                              i18n-pTooltip="@@benchmarks.runDetails.metric.wer" tooltipPosition="top">
                            <i class="pi pi-percentage col-icon"></i>WER
                          </th>
                          <th style="width: 6rem; text-align: right;" pTooltip="Character Error Rate"
                              i18n-pTooltip="@@benchmarks.runDetails.metric.cer" tooltipPosition="top">
                            <i class="pi pi-percentage col-icon"></i>CER
                          </th>
                          <th style="width: 6rem; text-align: right;" pTooltip="Real-time factor"
                              i18n-pTooltip="@@benchmarks.runDetails.metric.rtfx" tooltipPosition="top">
                            <i class="pi pi-forward col-icon"></i>RTFx
                          </th>
                          <th style="width: 8rem; text-align: right;">
                            <i class="pi pi-hourglass col-icon"></i>
                            <span i18n="@@benchmarks.runDetails.col.duration">Duration</span>
                          </th>
                        }
                        @if (isLlm()) {
                          <th style="width: 8rem; text-align: right;" pTooltip="Time to first token"
                              i18n-pTooltip="@@benchmarks.runDetails.metric.ttft" tooltipPosition="top">
                            <i class="pi pi-bolt col-icon"></i>TTFT
                          </th>
                          <th style="width: 7rem; text-align: right;" pTooltip="Tokens per second"
                              i18n-pTooltip="@@benchmarks.runDetails.metric.tps" tooltipPosition="top">
                            <i class="pi pi-gauge col-icon"></i>t/s
                          </th>
                          <th style="width: 8rem; text-align: right;">
                            <i class="pi pi-hashtag col-icon"></i>
                            <span i18n="@@benchmarks.runDetails.col.tokens">Tokens (in/out)</span>
                          </th>
                          <th>
                            <i class="pi pi-comment col-icon"></i>
                            <span i18n="@@benchmarks.runDetails.col.completion">Completion</span>
                          </th>
                        }
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-res>
                      <tr>
                        <td>{{ res.sort_index }}</td>
                        <td>
                          <p-tag
                            [value]="res.status"
                            [severity]="statusSeverity(res.status)"
                            [rounded]="true">
                          </p-tag>
                          @if (res.error_message) {
                            <i class="pi pi-info-circle error-info"
                               [pTooltip]="res.error_message"
                               tooltipPosition="right"></i>
                          }
                        </td>
                        @if (isStt()) {
                          <td>
                            <div class="sample-title">
                              {{ res.stt_benchmark_sample_title || '—' }}
                              @if (res.stt_benchmark_sample_case) {
                                <p-tag
                                  [value]="caseLabel(res.stt_benchmark_sample_case)"
                                  severity="info"
                                  [rounded]="true">
                                </p-tag>
                              }
                            </div>
                            @if (res.hypothesis) {
                              <div class="sample-hyp" [class.expanded]="isResultExpanded(res.id)">
                                <span class="dim" i18n="@@benchmarks.runDetails.hyp">hyp:</span>
                                {{ res.hypothesis }}
                              </div>
                              @if ((res.hypothesis.length ?? 0) > 120) {
                                <button type="button" class="text-toggle"
                                        (click)="toggleResult(res.id)">
                                  @if (isResultExpanded(res.id)) {
                                    <i class="pi pi-chevron-up"></i>
                                    <span i18n="@@benchmarks.runDetails.collapse">Свернуть</span>
                                  } @else {
                                    <i class="pi pi-chevron-down"></i>
                                    <span i18n="@@benchmarks.runDetails.expand">Показать полностью</span>
                                  }
                                </button>
                              }
                            }
                          </td>
                          <td style="text-align: right;">{{ formatErrorRate(res.wer) }}</td>
                          <td style="text-align: right;">{{ formatErrorRate(res.cer) }}</td>
                          <td style="text-align: right;">{{ formatRtfx(res.rtfx) }}</td>
                          <td style="text-align: right;">{{ formatMs(res.duration_ms) }}</td>
                        }
                        @if (isLlm()) {
                          <td style="text-align: right;">{{ formatMs(res.ttft_ms) }}</td>
                          <td style="text-align: right;">{{ formatTps(res.tokens_per_sec) }}</td>
                          <td style="text-align: right;">
                            {{ res.prompt_tokens ?? '—' }}<span class="dim"> / {{ res.completion_tokens ?? '—' }}</span>
                          </td>
                          <td>
                            @if (res.completion) {
                              <div class="completion-cell" [class.expanded]="isResultExpanded(res.id)">
                                {{ res.completion }}
                              </div>
                              @if ((res.completion.length ?? 0) > 120) {
                                <button type="button" class="text-toggle"
                                        (click)="toggleResult(res.id)">
                                  @if (isResultExpanded(res.id)) {
                                    <i class="pi pi-chevron-up"></i>
                                    <span i18n="@@benchmarks.runDetails.collapse">Свернуть</span>
                                  } @else {
                                    <i class="pi pi-chevron-down"></i>
                                    <span i18n="@@benchmarks.runDetails.expand">Показать полностью</span>
                                  }
                                </button>
                              }
                            } @else {
                              <span class="dim">—</span>
                            }
                          </td>
                        }
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                      <tr>
                        <td [attr.colspan]="isStt() ? 7 : 6" style="text-align: center;"
                            i18n="@@benchmarks.runDetails.noResults">
                          Результатов пока нет — прогон в процессе или прервался до первого сэмпла.
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>
              }
            </div>
          }
        } @empty {
          <div class="empty-items" i18n="@@benchmarks.runDetails.noItems">
            К этому прогону не привязано ни одной версии модели.
          </div>
        }
      </div>
    }
  </div>
</p-drawer>
